import { defineStore } from 'pinia'
import { reactive } from 'vue'

import {
  getNotifications,
  markAllNotificationsAsRead,
  updateNotificationReadStatus,
} from '@/api/notifications.api'
import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { notificationsUnavailableState } from '@/lib/services/notification-availability.service'
import { type NotificationType, RequestStatus } from '@/lib/types'
import { useErrorStore } from '@/stores/error.stores'
import { useUserStore } from '@/stores/user.stores'

type NotificationStoreData = {
  status: RequestStatus
  page: number
  hasMore: boolean
  data: NotificationType[]
}

export const useNotificationStore = defineStore('notification', () => {
  const errorStore = useErrorStore()
  const userStore = useUserStore()
  const all = reactive<NotificationStoreData>({
    status: RequestStatus.Idle,
    page: 1,
    hasMore: true,
    data: [],
  })
  const unread = reactive<NotificationStoreData & { total: number }>({
    status: RequestStatus.Idle,
    page: 1,
    hasMore: true,
    data: [],
    total: 0,
  })

  const fetchAll = async (reset = false) => {
    try {
      if (
        all.status === RequestStatus.Loading ||
        (all.status === RequestStatus.Success && !all.hasMore && !reset)
      ) {
        return
      }

      all.status = RequestStatus.Loading
      const page = reset ? 1 : all.page
      const { data, hasMore } = await getNotifications(userStore.subscriberId, page, undefined)
      all.data = reset ? data : [...all.data, ...data]
      all.page = page + 1
      all.hasMore = hasMore
      all.status = RequestStatus.Success
    } catch {
      all.status = RequestStatus.Error
    }
  }

  const fetchUnread = async (reset = false) => {
    try {
      if (
        unread.status === RequestStatus.Loading ||
        (unread.status === RequestStatus.Success && !unread.hasMore && !reset)
      ) {
        return
      }

      unread.status = RequestStatus.Loading
      const page = reset ? 1 : unread.page
      const { data, hasMore } = await getNotifications(userStore.subscriberId, page, false)
      unread.data = reset ? data : [...unread.data, ...data]
      unread.page = page + 1
      unread.hasMore = hasMore
      unread.status = RequestStatus.Success
    } catch {
      unread.status = RequestStatus.Error
    }
  }

  const markAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(userStore.subscriberId)
      all.data = all.data.map((notification) => ({ ...notification, read: true }))
      unread.data = []
      unread.total = 0
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
    }
  }

  const updateReadStatus = async (notificationId: string, read: boolean) => {
    try {
      const status = read ? 'read' : 'unread'
      await updateNotificationReadStatus(userStore.subscriberId, notificationId, status)
      all.data = all.data.map((n) => (n.id === notificationId ? { ...n, read } : n))
      if (status === 'read') {
        unread.data = unread.data.filter((n) => n.id !== notificationId)
      } else {
        const unreadNotification = all.data.find((n) => n.id === notificationId)
        if (unreadNotification) {
          unread.data = [unreadNotification, ...unread.data].sort(
            (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
          )
        }
      }
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
    }
  }

  const changeUnreadCount = (count: number) => (unread.total = count)

  return {
    all,
    unread,
    notificationsUnavailable: notificationsUnavailableState,
    fetchAll,
    fetchUnread,
    markAllAsRead,
    updateReadStatus,
    changeUnreadCount,
  }
})
